import React, { createRef, useEffect, useState, useMemo, createContext, useContext } from 'react';
import { MarkerClusterer, GridAlgorithm } from '@googlemaps/markerclusterer';

import { loadScript } from '$utilities/requests';
import { useLazyEffect } from '$hooks/lifecycle';

/* ========================================================================= */
/* Configuration */
/* ========================================================================= */

const MAX_ZOOM = 12;
const DEFAULT_COORD = {
    lat: 51.57964789108832,
    lng: -0.07474103400491047
};
const algorithm = new GridAlgorithm({
    gridSize: 80,
});

/* ========================================================================= */
/* Styles */
/* ========================================================================= */

const mapStyles = [
    {
        'featureType': 'all',
        'elementType': 'labels.text.fill',
        'stylers': [
            {
                'color': '#444444'
            }
        ]
    },
    {
        'featureType': 'landscape',
        'elementType': 'all',
        'stylers': [
            {
                'color': '#F0F0F0'
            }
        ]
    },
    {
        'featureType': 'landscape.man_made',
        'elementType': 'geometry',
        'stylers': [
            {
                'color': '#F0F0F0'
            },
            {
                'visibility': 'on'
            }
        ]
    },
    {
        'featureType': 'landscape.natural',
        'elementType': 'geometry',
        'stylers': [
            {
                'color': '#DBDBD9'
            },
            {
                'visibility': 'on'
            }
        ]
    },
    {
        'featureType': 'landscape.natural.landcover',
        'elementType': 'geometry',
        'stylers': [
            {
                'color': '#D4D4D2'
            },
            {
                'visibility': 'on'
            }
        ]
    },
    {
        'featureType': 'poi',
        'elementType': 'all',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'road',
        'elementType': 'geometry',
        'stylers': [
            {
                'saturation': -100,
            },
            {
                'lightness': 45
            }
        ]
    },
    {
        'featureType': 'road',
        'elementType': 'labels',
        'stylers': [
            {
                'visibility': 'simplified',
                'color': '#444444'
            }
        ]
    },
    {
        'featureType': 'transit',
        'elementType': 'all',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'water',
        'elementType': 'all',
        'stylers': [
            {
                'color': '#E6E6E6'
            }
        ]
    }
];

/* ========================================================================= */
/* Shared variables */
/* ========================================================================= */

const sharedMemory = {
    isRequested: false,
    isLoaded: false,
    queue: []
};

let markers = [];
let markerCluster;

/* ========================================================================= */
/* Hooks and functions */
/* ========================================================================= */

const Context = createContext();

/**
 * @function getIcon
 * @param {string} look - The reference to the map container.
 * @param {object} google - The map instance.
 * @return {object} - The icon object.
 *
 * Returns icon
 */
const getIcon = (look, google) => {
    switch (look) {
        case 'CIRCLE':
            return {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 25,
                fillColor: '#FFFFFF',
                fillOpacity: 1,
                strokeWeight: 0
            };
        case 'CAFE':
            return {
                scale: 30,
                url: '/images/Cafe.svg',
                fillColor: '#2F2F2F',
                fillOpacity: 1,
                strokeWeight: 0,
            };
        case 'LANDMARK':
            return {
                scale: 30,
                url: '/images/Landmark.svg',
                fillColor: '#2F2F2F',
                fillOpacity: 1,
                strokeWeight: 0,
            };
        case 'MUSEUM':
            return {
                scale: 30,
                url: '/images/Museum.svg',
                fillColor: '#2F2F2F',
                fillOpacity: 1,
                strokeWeight: 0,
            };
        case 'PARK':
            return {
                scale: 30,
                url: '/images/Park.svg',
                fillColor: '#2F2F2F',
                fillOpacity: 1,
                strokeWeight: 0,
            };
        case 'PIN':
            return {
                scale: 10,
                url: '/images/location-pin-large.svg',
                fillColor: '#2F2F2F',
                fillOpacity: 1,
                strokeWeight: 0,
            };
        case 'SCHOOL':
            return {
                scale: 30,
                url: '/images/School.svg',
                fillColor: '#2F2F2F',
                fillOpacity: 1,
                strokeWeight: 0,
            };
        case 'SHOP':
            return {
                scale: 30,
                url: '/images/Shop.svg',
                fillColor: '#2F2F2F',
                fillOpacity: 1,
                strokeWeight: 0,
            };
        case 'TUBE':
            return {
                scale: 30,
                url: '/images/Tube.svg',
                fillColor: '#2F2F2F',
                fillOpacity: 1,
                strokeWeight: 0,
            };
        default:
            return {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 25,
                fillColor: '#FFFFFF',
                fillOpacity: 1,
                strokeWeight: 0
            };
    }
};

/**
 * @function createMap
 * @param {object} mapRef - The reference to the map container.
 * @param {object}
 *     @prop {string} lat - The latitude on which center the map.
 *     @prop {string} lng - The longitude on which center the map.
 * @return {object} - The map instance.
 *
 * Creates a Google Map instance with the provided params.
 */
const createMap = (mapRef, { lat, lng }) => {
    return new window.google.maps.Map(
        mapRef.current,
        {
            center: { lat, lng },
            fullscreenControl: false,
            mapTypeControl: false,
            mapTypeId: window.google.maps.MapTypeId.ROADMAP,
            maxZoom: 18,
            minZoom: 0,
            scaleControl: false,
            scrollWheel: true,
            scrollWheelZoom:'center',
            streetViewControl: false,
            zoom: 3,
            zoomControl: false,
            styles: mapStyles,
        }
    );
};

/**
 * @function createMarker
 * @param {object} googleMapInstance - The Google Map instance.
 * @param {object}
 *     @prop {string} lat - The latitude on which center the map.
 *     @prop {string} lng - The longitude on which center the map.
 * @return {object} - The marker instance.
 *
 * Creates a marker instance with the provided params.
 */
const createMarker = (googleMapInstance, { lat, lng, id, setActiveItem, activeItem, isClusterable, type }) => {
    const icon = isClusterable ?
        activeItem === id ? getIcon('PIN', google) : getIcon('CIRCLE', google) : getIcon(type, google);

    const marker = new window.google.maps.Marker({
        map: googleMapInstance,
        position: { lat, lng },
        id: id,
        icon: icon,
        size: new google.maps.Size(20, 20),
        origin: new google.maps.Point(10, 10),
        zIndex: id === activeItem ? 100 : markers.length,
        title: `map-marker-${activeItem === id ? type : ''}`,
        label: isClusterable && activeItem !==id ? '1' : ''
    });

    marker.addListener('click', () => {
        setActiveItem(id);
        googleMapInstance.panTo({lat, lng});
    });

    markers.push(marker);
    return marker;
};

/**
 * @function customControls
 * Creates custom zoom icons
 */
const customControls = (controlDiv, map) => {
    // Creating divs & styles for custom zoom control
    controlDiv.style.padding = '5px';

    // Set CSS for the control wrapper
    const controlWrapper = document.createElement('div');
    controlWrapper.classList.add('zoom-wrapper');
    controlDiv.appendChild(controlWrapper);

    const zoomOutButton = document.createElement('div');
    zoomOutButton.classList.add('zoom-out-button');
    controlWrapper.appendChild(zoomOutButton);

    const zoomInButton = document.createElement('div');
    zoomInButton.classList.add('zoom-in-button');
    controlWrapper.appendChild(zoomInButton);

    // Setup the click event listener - zoomIn
    zoomInButton.addEventListener('click', function() {
        map.setZoom(map.getZoom() + 1);
    });

    // Setup the click event listener - zoomOut
    zoomOutButton.addEventListener('click', function() {
        map.setZoom(map.getZoom() - 1);
    });
};

/**
 * @function drawMarkers
 * @param {object} googleMapInstance - The Google Map instance.
 * @param {array} locations
 *
 * Creates a set of markers and zooms the map to fit them in view
 */
const drawMarkers = (googleMapInstance, options) => {
    const { locations = [], setActiveItem, activeItem, isClusterable } = options;

    if (locations.length <= 0) {
        return;
    }

    locations.map((location) => {
        return createMarker(googleMapInstance,
            { id: location.id,
                lat: parseFloat(location.lat),
                lng: parseFloat(location.lng),
                setActiveItem,
                activeItem,
                isClusterable,
                type: location.type
            });
    });

    let bounds = new google.maps.LatLngBounds();

    locations.forEach((location) => {
        bounds.extend({ lat: parseFloat(location.lat), lng: parseFloat(location.lng) });
    });


    googleMapInstance.fitBounds(bounds);

    const zoom = googleMapInstance.getZoom();
    googleMapInstance.setZoom(zoom > MAX_ZOOM ? MAX_ZOOM : zoom);
};

const recenterMap = (center, googleMapInstance) => {
    googleMapInstance.panTo(center);
};

/**
 * @hook useGoogleMap
 * @param {object} mapRef - The reference to the map container
 * @param {object} mapOptions
 *     @prop {string} lat
 *     @prop {string} lng
 *     @prop {string} key - The API key provided by Google.
 * @return {object|null} - The Google map instance
 *
 * 1. Initialises Google Maps when the component is mounted in the browser.
 * 1.1. If the Google Maps scripts are not loaded yet, let’s load it and execute the queue once initialised.
 * 1.2. Otherwise, let’s initialise the Google Map instance on load.
 */
const useGoogleMap = (mapRef, mapOptions) => {
    const [ googleMap, setGoogleMap ] = useState(null);

    const init = () => {
        const googleMapInstance = createMap(mapRef, mapOptions);

        const customZoomDiv = document.createElement('div');
        customControls(customZoomDiv, googleMapInstance);

        googleMapInstance.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(customZoomDiv);
        setGoogleMap(googleMapInstance);

        drawMarkers(googleMapInstance, mapOptions);
        if (mapOptions.isClusterable) {
            const renderer = {
                render: ({ count, position }) =>
                    new google.maps.Marker({
                        icon: getIcon('CIRCLE', google),
                        label: { text: String(count), color: 'black', fontSize: '14px', fontFamily: 'Founders Grotesk' },
                        position,
                        zIndex: Number(markers.length) + count,
                    }),
            };
            markerCluster = new MarkerClusterer({ markers, map:googleMapInstance, renderer, algorithm });
        }
    };

    /* [1] */
    useEffect(() => {
        if (!sharedMemory.isRequested && !window.google?.maps && mapOptions.key) {
            sharedMemory.isRequested = true;
            sharedMemory.queue.push(init);

            /* [1.1] */
            loadScript('https://maps.googleapis.com/maps/api/js?key=' + mapOptions.key + '&libraries=geometry', () => {
                sharedMemory.isLoaded = true;
                sharedMemory.queue.forEach(callback => callback());
            });
        } else if (!googleMap && mapRef.current) {
            /* [1.2] */
            sharedMemory.isLoaded ? init() : sharedMemory.queue.push(init);
        }
    }, []);

    return googleMap;
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */
/**
 * @component Map
 * @props (see propTypes)
 *
 * 1. Initialises Google Maps.
 */
const Map = ({ dev, staging, lat = DEFAULT_COORD.lat , lng = DEFAULT_COORD.lng, locations, activeItem , setActiveItem, center, setMap = () => {}, isClusterable, ...remainingProps }) => {
    const mapRef = createRef(null);
    const [ mapType, setMapType ] = useState('roadmap');
    /* [1] */
    const googleMap = useGoogleMap(mapRef, {
        locations: locations,
        lat: parseFloat(lat),
        lng: parseFloat(lng),
        key: 'AIzaSyBQfzQhBmFhT9_YJzsyWWZX4qdFx6UG0WM',
        activeItem: activeItem,
        setActiveItem: setActiveItem,
        isClusterable: isClusterable,
        ...remainingProps
    });

    useEffect(() => {
        if(googleMap && google) {
            setMap(google);
        }
    }, [googleMap]);

    // useEffect(() => {
    //     if (googleMap && center?.lat && center?.lng) {
    //         recenterMap(center, googleMap);
    //     }
    // }, [center]);


    // useLazyEffect(() => {
    //     switch (mapType) {
    //         case 'roadmap':
    //             googleMap.setMapTypeId(google.maps.MapTypeId.ROADMAP);
    //             break;
    //         case 'satellite':
    //             googleMap.setMapTypeId(google.maps.MapTypeId.SATELLITE);
    //             break;
    //         case 'hybrid':
    //             googleMap.setMapTypeId(google.maps.MapTypeId.HYBRID);
    //             break;
    //         case 'terrain':
    //             googleMap.setMapTypeId(google.maps.MapTypeId.TERRAIN);
    //             break;
    //     }
    // }, [ mapType ]);

    // useEffect(() => {
    //     if (googleMap && activeItem) {
    //         const newGridSize = new GridAlgorithm({
    //             gridSize: 60,
    //         });
    //         // clear all current markers
    //         markers.forEach(marker => {
    //             marker.setMap(null);
    //         });
    //         markerCluster.setMap(null);
    //         markers = [];

    //         // redraw markers
    //         if(locations.length > 0) {
    //             locations.map((location) => {
    //                 return createMarker(googleMap, { id: location.id, lat: parseFloat(location.lat), lng: parseFloat(location.lng), setActiveItem, activeItem, isClusterable, type: location.type });
    //             });
    //         }

    //         if (isClusterable) {
    //             try {
    //                 markerCluster.clearMarkers(true);
    //             } catch(e) {
    //                 console.log('activeItem', e);
    //             }

    //             // redraw clusters without active marker
    //             const renderer = {
    //                 render: ({ count, position }) =>
    //                     new google.maps.Marker({
    //                         icon: getIcon('CIRCLE', google),
    //                         label: { text: String(count), color: 'black', fontSize: '14px', fontFamily: 'Founders Grotesk' },
    //                         position,
    //                         zIndex: Number(markers.length) + count,
    //                     }),
    //             };
    //             const newCluster = markers.filter(marker => marker.id !== activeItem);
    //             markerCluster = new MarkerClusterer({ markers: newCluster, map:googleMap, renderer, algorithm: newGridSize });

    //             // center map
    //             const newCenter = locations.find(location => location.id === activeItem);
    //             if (newCenter) {
    //                 googleMap.panTo({ lat: newCenter.lat, lng: newCenter.lng });
    //                 let bounds = new google.maps.LatLngBounds();

    //                 bounds.extend({ lat: parseFloat(newCenter.lat), lng: parseFloat(newCenter.lng) });
    //                 googleMap.fitBounds(bounds);
    //             }
    //         }
    //     }

    // }, [activeItem]);

    // useEffect(() => {
    //     if (googleMap && isClusterable) {
    //         // clear all current markers
    //         markers.forEach(marker => {
    //             marker.setMap(null);
    //         });
    //         markerCluster.setMap(null);
    //         markers = [];

    //         // redraw markers
    //         if(locations.length > 0) {
    //             locations.map((location) => {
    //                 return createMarker(googleMap, { id: location.id, lat: parseFloat(location.lat), lng: parseFloat(location.lng), setActiveItem, activeItem });
    //             });
    //         }

    //         // redraw clusters without active marker
    //         const renderer = {
    //             render: ({ count, position }) =>
    //                 new google.maps.Marker({
    //                     icon: getIcon('CIRCLE', google),
    //                     label: { text: String(count), color: 'black', fontSize: '14px', fontFamily: 'Founders Grotesk' },
    //                     position,
    //                     zIndex: Number(markers.length) + count,
    //                 }),
    //         };
    //         markerCluster = new MarkerClusterer({ markers, map: googleMap, renderer, algorithm });
    //         googleMap.panTo({ lat: locations.length > 0 ? lat : DEFAULT_COORD.lat, lng: locations.length > 0 ? lng : DEFAULT_COORD.lat });
    //     }
    // }, [locations.length]);


    const context = useMemo(() => ({ googleMap, mapType, setMapType, mapRef }), [ googleMap, mapType ]);

    return (
        <Context.Provider value={context}>
            <div {...remainingProps} />
        </Context.Provider>
    );
};

const MapCanvas = props => {
    const { mapRef } = useContext(Context);

    return (
        <div
            ref={mapRef}
            {...props} />
    );
};

const MapTypeButton = ({ type, ...remainingProps }) => {
    const { mapType, setMapType } = useContext(Context);

    return (
        <button
            aria-disabled={mapType === type}
            onClick={() => setMapType(type)}
            type='button'
            {...remainingProps} />
    );
};


/* ========================================================================= */
/* Typing & exports */
/* ========================================================================= */

Map.propTypes = {};

MapCanvas.propTypes = {};

MapTypeButton.propTypes = {};

export {
    Map,
    MapCanvas,
    MapTypeButton,
};
